import type { ReactElement } from 'react';

import { BooleanFilter, ensureDefined, invariant } from '@e-socna/utils';
import { drillFragmentData, getFragmentData } from '@e-socna/data-layer';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';

import { useMissionContextOptional } from '../../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionContextStatus } from '../../../../contexts/MissionContext/types/mission-context.type';
import { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../../../../pages/Folder/fragments/folder-business-entity-consolidation.fragment';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MissionPage_GeneralInfo_Mission_Fragment } from '../../../../pages/Mission/GeneralInfo';
import { MissionPage_Tabs_Mission_Fragment } from '../../../../pages/Mission/Tabs';

import { AddressWithMapCmp_Address_Fragment } from '../../../AddressWithMap';

import type {
  ResolverParameters,
  ResolverResult,
} from '../../types/resolvers.type';

import { DevisPrestationsServiceUsages } from '../components/DevisPrestationsServiceUsages';
import type { DevisPrestationsLayout } from '../components/DevisPrestationsServiceUsages';
import { buildDevisPrestationsRenderModel } from '../utils/build-devis-prestations-render-model.util';

import { useFullyConsolidatedServiceUsages } from './useFullyConsolidatedServiceUsages.hook';

export const useDevisPrestationsContent = (
  parameters: ResolverParameters,
  layout: DevisPrestationsLayout,
): ResolverResult<ReactElement> => {
  const { currenciesIdMap, projectionsCodeMap, unitsIdMap, vatsIdMap } =
    useReferencesContext();

  const missionContext = useMissionContextOptional();

  const fullyConsolidated = useFullyConsolidatedServiceUsages(parameters);

  if (!fullyConsolidated.isSuccess) {
    return {
      ...fullyConsolidated,
      data: undefined,
    };
  }

  invariant(!!missionContext, 'Mission context is required');

  const { status, mission } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      const { title } = mission.consolidation;

      const generalInfo = drillFragmentData(
        [
          MissionPage_Tabs_Mission_Fragment,
          MissionPage_GeneralInfo_Mission_Fragment,
        ],
        mission,
      );

      // A collaboration has no category.
      const missionCategoryLeaf = generalInfo.categoryLeaf;

      const consolidation = getFragmentData(
        FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment,
        mission.consolidation,
      );

      const address = getFragmentData(
        AddressWithMapCmp_Address_Fragment,
        consolidation.address,
      );

      const showPostalInfo = ({ postalInfo }: typeof address) => {
        if (!postalInfo) {
          return null;
        }

        return (
          <>
            <strong>À l&apos;adresse suivante :</strong>
            {[
              postalInfo.building,
              <br key="1" />,
              `${postalInfo.streetNumber} ${postalInfo.route}`,
              <br key="2" />,
              `${postalInfo.postalCode} ${postalInfo.locality}`,
            ].filter(BooleanFilter)}
          </>
        );
      };

      const showCadastralInfo = ({ cadastralInfo }: typeof address) => {
        if (!cadastralInfo) {
          return null;
        }

        return (
          <>
            <strong>À la parcelle cadastrale suivante :</strong>,
            {[
              cadastralInfo.departmentCode,
              cadastralInfo.municipalityCode,
              cadastralInfo.oldMunicipalityCode,
              cadastralInfo.districtCode,
              cadastralInfo.section,
              cadastralInfo.number,
              cadastralInfo.sheet,
            ].filter(BooleanFilter)}
          </>
        );
      };

      const showCoordinates = ({ locationInfo }: typeof address) => {
        if (!locationInfo?.coordinates) {
          return null;
        }

        const isLatLon =
          locationInfo.projectionId === projectionsCodeMap.Epsg4326._id;

        const { coordinates0, coordinates1 } = ensureDefined({
          coordinates0: locationInfo.coordinates[0],
          coordinates1: locationInfo.coordinates[1],
        });

        return (
          <>
            <strong>Aux coordonnées suivantes :</strong>,
            {isLatLon
              ? `Latitude : ${String(coordinates1)}, Longitude : ${String(coordinates0)}`
              : `X : ${String(coordinates0)}, Y : ${String(coordinates1)}`}
            ,
          </>
        );
      };

      const addressContent = showPostalInfo(address) ??
        showCadastralInfo(address) ??
        showCoordinates(address) ?? ['<strong>Adresse non renseignée<strong>'];

      const { serviceUsagesGroups } = ensureDefined({
        serviceUsagesGroups: fullyConsolidated.data?.serviceUsagesGroups,
      });

      const renderModel = buildDevisPrestationsRenderModel(serviceUsagesGroups);

      const content = (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <div style={{ width: '100%', textAlign: 'center', margin: '0 auto' }}>
            <p>
              {missionCategoryLeaf ? (
                <>
                  <strong>{missionCategoryLeaf}</strong>
                  <br />
                </>
              ) : null}
              <strong>{title}</strong>
              <br />
              {addressContent}
            </p>
          </div>
          <DevisPrestationsServiceUsages
            currenciesIdMap={currenciesIdMap}
            layout={layout}
            renderModel={renderModel}
            unitsIdMap={unitsIdMap}
            vatsIdMap={vatsIdMap}
          />
        </div>
      );

      return {
        data: { generated: content },
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
